/**
 * サーバー時刻の見積もり
 * 端末の時計は合っているとは限らないため、受け取ったサーバー時刻と performance.now() の差で現在を見積もります。
 * 画面へ戻った時とスリープを検知した時は基準を捨て、取り直すまで時刻を返しません（残り時間を出さないため）。
 */

/** 壁時計と経過時間のずれをスリープとみなす幅。Date.now() はこの検知にだけ使い、時刻の正本にしない */
const SLEEP_DETECTION_TOLERANCE_MS = 5 * 1000;

/** 購読者へ時刻の進みを知らせる間隔 */
const TICK_INTERVAL_MS = 1000;

/** 時刻の基準 */
type ClockAnchor = {
  /** 受け取ったサーバー時刻（epoch ms） */
  serverMs: number;
  /** 受け取った時点の performance.now() */
  performanceMs: number;
  /** 受け取った時点の Date.now()。スリープの検知だけに使う */
  wallMs: number;
};

/** 時刻の基準。まだ受け取っていない・捨てた後はnull */
let anchor: ClockAnchor | null = null;
/** 時刻の更新の購読者 */
const listeners = new Set<() => void>();
/** 購読中だけ動かす時刻の進みのタイマー */
let tickTimer: ReturnType<typeof setInterval> | undefined;
/** 画面復帰の監視を始めたか */
let isWatchingResume = false;
/** 画面を離れるたびに進む版。離れる前に始めた取得の時刻を、戻った後の基準にしないために使う */
let clockEpoch = 0;

/**
 * 購読者へ通知する
 * @returns {void}
 */
const notifyListeners = (): void => {
  listeners.forEach((listener) => listener());
};

/**
 * 時刻の基準を捨てる（次にサーバー時刻を受け取るまで時刻を返さない）
 * @returns {void}
 */
export const invalidateServerClock = (): void => {
  if (!anchor) return;
  anchor = null;
  notifyListeners();
};

/**
 * 画面を離れたときに版を進め、戻ったときに基準を捨てる
 * @returns {void}
 */
const handleVisibilityChange = (): void => {
  if (document.visibilityState === 'hidden') {
    clockEpoch += 1;
  } else {
    invalidateServerClock();
  }
};

/**
 * ページキャッシュから戻ったときに基準を捨てる
 * @param {PageTransitionEvent} event - pageshowイベント
 * @returns {void}
 */
const handlePageShow = (event: PageTransitionEvent): void => {
  if (!event.persisted) return;
  clockEpoch += 1;
  invalidateServerClock();
};

/**
 * 画面復帰の監視を始める
 * @returns {void}
 */
const watchResume = (): void => {
  if (isWatchingResume) return;
  isWatchingResume = true;
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pageshow', handlePageShow);
};

/**
 * 今の版を返す。取得を始める前に控え、syncServerClock へ渡す
 * @returns {number} 版
 */
export const getServerClockEpoch = (): number => {
  watchResume();
  return clockEpoch;
};

/**
 * サーバー時刻を受け取って基準にする。読めない値は無視する（例外を投げない）
 * @param {unknown} serverTime - サーバー時刻（ISO 8601）
 * @param {number} fetchEpoch - 取得を始めた時の版。その後に画面を離れていれば、古い時刻なので無視する
 * @returns {void}
 */
export const syncServerClock = (serverTime: unknown, fetchEpoch: number = clockEpoch): void => {
  /** サーバー時刻（epoch ms） */
  const serverMs = typeof serverTime === 'string' ? Date.parse(serverTime) : Number.NaN;
  if (!Number.isFinite(serverMs) || fetchEpoch !== clockEpoch) return;
  anchor = { serverMs, performanceMs: performance.now(), wallMs: Date.now() };
  watchResume();
  notifyListeners();
};

/**
 * 見積もったサーバー時刻を返す
 * @returns {number | null} サーバー時刻（epoch ms）。基準が無い・スリープを検知した場合はnull
 */
export const getServerNowMs = (): number | null => {
  if (!anchor) return null;
  /** 基準からの経過時間 */
  const elapsedMs = performance.now() - anchor.performanceMs;
  /** performance.now() はスリープ中に止まる端末がある。壁時計と食い違ったら、取り直すまで信用しない */
  const isDrifted = Math.abs(Date.now() - anchor.wallMs - elapsedMs) > SLEEP_DETECTION_TOLERANCE_MS;
  return isDrifted ? null : anchor.serverMs + elapsedMs;
};

/**
 * 時刻の更新を購読する。購読中は時刻の進みも通知する
 * @param {() => void} listener - 通知の受け手
 * @returns {() => void} 購読解除
 */
export const subscribeServerClock = (listener: () => void): (() => void) => {
  listeners.add(listener);
  tickTimer ??= setInterval(notifyListeners, TICK_INTERVAL_MS);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      clearInterval(tickTimer);
      tickTimer = undefined;
    }
  };
};
